/* eslint-disable complexity */
import React, { useState } from 'react';
import classNames from 'classnames';
import { differenceInHours, format } from 'date-fns';
import { Link, useParams } from 'react-router-dom';

import Icon from 'carbon-react/lib/components/icon';
import Tooltip from 'carbon-react/lib/components/tooltip';

import { DispatchedReportItem, ReportScheduleItem } from '@futrli-sdk/report_scheduling';
import { FormattedMessage } from '@futrli/localisation';
import {
  invalidateAwaitingReviewReports,
  invalidateLiveReports,
  invalidateReportSchedules
} from '@futrli/views/mutations/invalidateListingQueries';
import { Logger, ROUTES } from '@futrli/shared-services';
import { toastError, toastSuccess } from '@futrli/ui-components';

import {
  deleteDispatchedReport,
  deleteScheduledReport,
  getScheduleReportDetails
} from '../../../core/api/reporting/report-scheduling';
import { DeleteScheduleReportDialog } from '../../../ui/virtualized-list/document-library/delete-schedule-report-dialog';
import { getHoursMinutes } from '../../../utils/dates';
import { getPeriodText } from '../document-library/utils';
import { NormalizedReportScheduleItem } from '../document-library/scheduled-reports/scheduled-reports';
import { ScheduleReportDialog } from '../schedule-report-dialog/schedule-report-dialog';
import { usePredictionsInfoContext } from '../../../forecast/detail/context/predictions-info-context';
import { useReportBuilderContext } from '../../report-builder.context';

import * as styles from './schedule-report-item.scss';

interface Props {
  isAwaitingReview?: boolean;
  scheduleReport: ReportScheduleItem | DispatchedReportItem;
  reportId: string;
}

const shouldShowNextSendingWarning = (date: string | null) => {
  let showNextSendingWarning = false;
  if (date) {
    const nextGeneratedDate = new Date(date);
    const diffInHours = differenceInHours(nextGeneratedDate, new Date());
    // eslint-disable-next-line no-magic-numbers
    showNextSendingWarning = diffInHours <= 24;
  }
  return showNextSendingWarning;
};

export const ScheduleReportItem = ({ isAwaitingReview, scheduleReport, reportId }: Props) => {
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [scheduledReportDetails, setScheduleReportDetails] = useState<NormalizedReportScheduleItem>();
  const [isOpenScheduleReportDialog, setIsOpenScheduleReportDialog] = useState(false);

  const { isConsolidation } = usePredictionsInfoContext();

  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisationId } = useParams();
  const workspaceId = tempWorkspaceId || '';
  const organisationId = tempOrganisationId || '';

  const { revalidateScheduleReportData } = useReportBuilderContext();

  const handleOpenDeleteDialog = () => {
    setIsOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setIsOpenDeleteDialog(false);
  };

  const handleOpenScheduleDialog = () => {
    setIsOpenScheduleReportDialog(true);
  };

  const handleCloseScheduleDialog = () => {
    setIsOpenScheduleReportDialog(false);
  };

  const handleDelete = async () => {
    try {
      setIsLoading(true);
      if (isAwaitingReview) {
        await deleteDispatchedReport(workspaceId, organisationId, scheduleReport.id, isConsolidation);
        invalidateAwaitingReviewReports(organisationId, workspaceId, isConsolidation);
      } else {
        await deleteScheduledReport(workspaceId, organisationId, scheduleReport.id, isConsolidation);
        invalidateReportSchedules(organisationId, workspaceId, isConsolidation);
      }
      invalidateLiveReports(organisationId, workspaceId, !!isConsolidation);
      revalidateScheduleReportData();
      handleCloseDeleteDialog();
      toastSuccess(<FormattedMessage id="reporting.schedule_deleted" defaultMessage="Schedule deleted" />);
    } catch (e) {
      Logger.error(e as string);
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditClick = async () => {
    try {
      setIsLoading(true);
      handleOpenScheduleDialog();
      const scheduleReportId = isAwaitingReview
        ? (scheduleReport as DispatchedReportItem).report_schedule_id
        : (scheduleReport as ReportScheduleItem).id;
      const fetchedScheduleReportDetails = await getScheduleReportDetails(
        workspaceId,
        organisationId,
        scheduleReportId,
        isConsolidation
      );
      setScheduleReportDetails({
        ...fetchedScheduleReportDetails,
        // TODO: what to use when  next_generate_at is null
        next_generate_at: fetchedScheduleReportDetails.next_generate_at
          ? new Date(fetchedScheduleReportDetails.next_generate_at)
          : fetchedScheduleReportDetails.end_date
          ? new Date(fetchedScheduleReportDetails.end_date)
          : new Date(fetchedScheduleReportDetails.start_date),
        users: fetchedScheduleReportDetails.users
          .map(u => u.email_address)
          .join(', ')
          .trim(),
        time: fetchedScheduleReportDetails.next_generate_at
          ? getHoursMinutes(fetchedScheduleReportDetails.next_generate_at)
          : fetchedScheduleReportDetails.end_date
          ? getHoursMinutes(fetchedScheduleReportDetails.end_date)
          : getHoursMinutes(fetchedScheduleReportDetails.start_date)
      });
    } catch (e) {
      Logger.error(e as string);
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.container} style={{ display: 'flex' }}>
      <div style={{ marginRight: 16 }}>
        {isAwaitingReview && (
          <div className={styles.iconContainer} style={{ cursor: 'auto' }}>
            <Icon type="warning" fontSize="small" bgSize="small" color="#FD0707" />
          </div>
        )}
        <div className={styles.iconContainer} onClick={handleEditClick}>
          <Icon type="edit" fontSize="small" bgSize="small" />
        </div>
        <div className={styles.iconContainer} onClick={handleOpenDeleteDialog}>
          <Icon type="delete" fontSize="small" bgSize="small" />
        </div>
      </div>
      <div>
        {isAwaitingReview && (
          <Link
            to={`/workspaces/${workspaceId}/organisations/${organisationId}${ROUTES.PREDICT}${ROUTES.REPORTING}${ROUTES.DOCUMENT_LIBRARY}${ROUTES.AWAITING_REVIEW}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: 'flex' }}
          >
            <div className={classNames(styles.label, styles.linkLabel)}>
              <FormattedMessage id="reporting.held_for_review" defaultMessage="Held for review" />
            </div>
            <Icon type="link" fontSize="small" bgSize="small" ml={1} />
          </Link>
        )}
        <div className={styles.label}>
          <FormattedMessage
            id="reporting.repeating_every_n"
            defaultMessage="Repeating: {repeatingValue}"
            values={{ repeatingValue: getPeriodText(scheduleReport.period, scheduleReport.repeating_frequency) }}
          />
        </div>
        <div className={styles.label}>
          <FormattedMessage id="reporting.ending" defaultMessage="Ending:" />{' '}
          {scheduleReport.end_type === 'does-not-end' && <FormattedMessage id="common.never" defaultMessage="Never" />}
          {scheduleReport.end_type === 'end-date' && (
            <FormattedMessage
              id="reporting.on_date_value"
              defaultMessage="On {dateValue}"
              values={{
                dateValue: format(new Date(scheduleReport.end_date || ''), 'd MMM yyyy')
              }}
            />
          )}
          {scheduleReport.end_type === 'end-after-occurrences' && (
            <FormattedMessage
              id="reporting.after_n_occurrences"
              defaultMessage="After {occurrences} occurrences"
              values={{ occurrences: scheduleReport.end_after_occurrences }}
            />
          )}
        </div>
        <div className={styles.label}>
          {scheduleReport.next_generate_at && (
            <FormattedMessage
              id="reporting.next_sending_pdf_on"
              defaultMessage="Next sending PDF on: {dateValue}"
              values={{
                dateValue: (
                  <span
                    style={shouldShowNextSendingWarning(scheduleReport.next_generate_at) ? { color: '#CD384B' } : {}}
                  >
                    {format(new Date(scheduleReport.next_generate_at || ''), 'd MMM yyyy')}
                  </span>
                )
              }}
            />
          )}
        </div>
        <div className={styles.label}>
          <Tooltip message={scheduleReport.users[0].email_address}>
            <div
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: 242
              }}
            >
              <FormattedMessage
                id="predict.to_value"
                defaultMessage="To: {firstUser}"
                values={{ firstUser: scheduleReport.users[0].email_address }}
              />
            </div>
          </Tooltip>
          {scheduleReport.users.length > 1
            ? scheduleReport.users.map((u, i) => {
                if (i === 0) {
                  return null;
                }
                return (
                  <Tooltip key={u.email_address} message={u.email_address}>
                    <div
                      style={{
                        marginLeft: 21,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        width: 220
                      }}
                    >
                      {u.email_address}
                    </div>
                  </Tooltip>
                );
              })
            : null}
        </div>
      </div>
      {isOpenDeleteDialog && (
        <DeleteScheduleReportDialog
          isOpen={isOpenDeleteDialog}
          handleCloseDialog={handleCloseDeleteDialog}
          onSubmit={handleDelete}
          isLoading={isLoading}
        />
      )}
      {isOpenScheduleReportDialog && (
        <ScheduleReportDialog
          isOpen={isOpenScheduleReportDialog}
          handleClose={handleCloseScheduleDialog}
          reportName={scheduleReport.name}
          reportId={reportId}
          initialData={scheduledReportDetails}
          revalidateData={revalidateScheduleReportData}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};
